import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, Edit, Trash2,
    Search, Phone, Mail, MapPin, Star, AlertCircle, FileText, Tag, Calendar,
    Filter, Eye, X, Contact, Building, Home, GraduationCap, Stethoscope,
    Briefcase, Wrench, Heart, UserPlus, Shield, Bell, Share2, Lock, Image as ImageIcon,
    Users, Plus
} from 'lucide-react';
import { fetchContacts, createContact, updateContact, deleteContact } from '../services/contactsApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';
import NavBar from './NavBar';

const ContactBook = () => {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const photoInputRef = useRef(null);

    // Contacts state
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [showContactDetailModal, setShowContactDetailModal] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterPriority, setFilterPriority] = useState('All');
    const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);

    const [newContact, setNewContact] = useState({
        name: '',
        category: 'Family',
        relationship: '',
        organization: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
        tags: [],
        photoUrl: null,
        isEmergency: false,
        priority: 'General',
        linkedMemberIds: [],
        visibility: 'Family',
        birthday: '',
        anniversary: '',
        verified: false,
        type: 'Person' // Person or Institution
    });

    // Load data
    useEffect(() => {
        loadFamilies();
    }, []);

    // Load contacts when family changes
    useEffect(() => {
        if (selectedFamily) {
            loadContacts();
        }
    }, [selectedFamily]);

    const loadFamilies = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to load families');
            }
            
            const familiesData = await response.json();
            setFamilies(familiesData);

            const selectedFamilyId = localStorage.getItem('selectedFamilyId');
            let familyToSelect = null;
            
            if (selectedFamilyId && familiesData.length > 0) {
                familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
            }
            
            if (!familyToSelect && familiesData.length === 1) {
                familyToSelect = familiesData[0];
                localStorage.setItem('selectedFamilyId', familiesData[0].id);
            }
            
            if (!familyToSelect && familiesData.length > 0) {
                localStorage.removeItem('selectedFamilyId');
            }
            
            setSelectedFamily(familyToSelect);
        } catch (error) {
            console.error('Error loading families:', error);
        }
    };

    const loadContacts = async () => {
        if (!selectedFamily) return;
        
        try {
            const response = await fetchContacts(selectedFamily.id);
            setContacts(response.items || []);
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    };

    // Removed saveContacts - now using API directly

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Photo handling
    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert('Photo size must be less than 2MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewContact({ ...newContact, photoUrl: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    // Contact CRUD
    const handleAddContact = async () => {
        if (!newContact.name.trim()) {
            alert('Please enter contact name');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const contactData = {
                name: newContact.name,
                contact_relationship: newContact.relationship || null,
                category: newContact.category || null,
                photo: newContact.photoUrl || null,
                phone: newContact.phone || null,
                email: newContact.email || null,
                address: newContact.address || null,
                website: null,
                is_emergency: newContact.isEmergency || false,
                priority_level: newContact.priority === 'High' ? 5 : newContact.priority === 'Medium' ? 3 : 1,
                linked_family_member_id: newContact.linkedMemberIds?.[0] || null,
                notes: newContact.notes || null,
                tags: newContact.tags?.join(',') || null
            };

            const createdContact = await createContact(selectedFamily.id, contactData);
            setContacts([...contacts, createdContact]);
            resetContactForm();
            setShowAddContactModal(false);
        } catch (error) {
            console.error('Error creating contact:', error);
            alert(`Failed to create contact: ${error.message}`);
        }
    };

    const handleUpdateContact = async () => {
        if (!newContact.name.trim()) {
            alert('Please enter contact name');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const contactData = {
                name: newContact.name,
                contact_relationship: newContact.relationship || null,
                category: newContact.category || null,
                photo: newContact.photoUrl || null,
                phone: newContact.phone || null,
                email: newContact.email || null,
                address: newContact.address || null,
                website: null,
                is_emergency: newContact.isEmergency || false,
                priority_level: newContact.priority === 'High' ? 5 : newContact.priority === 'Medium' ? 3 : 1,
                linked_family_member_id: newContact.linkedMemberIds?.[0] || null,
                notes: newContact.notes || null,
                tags: newContact.tags?.join(',') || null
            };

            const updatedContact = await updateContact(selectedFamily.id, editingContact.id, contactData);
            const updated = contacts.map(c => c.id === editingContact.id ? updatedContact : c);
            setContacts(updated);
            resetContactForm();
            setShowAddContactModal(false);
            setEditingContact(null);
        } catch (error) {
            console.error('Error updating contact:', error);
            alert(`Failed to update contact: ${error.message}`);
        }
    };

    const handleDeleteContact = async (id) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteContact(selectedFamily.id, id);
            setContacts(contacts.filter(c => c.id !== id));
            if (selectedContact?.id === id) {
                setSelectedContact(null);
                setShowContactDetailModal(false);
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            alert(`Failed to delete contact: ${error.message}`);
        }
    };

    const handleToggleEmergency = async (id) => {
        if (!selectedFamily) return;
        
        const contact = contacts.find(c => c.id === id);
        if (!contact) return;
        
        try {
            const contactData = {
                name: contact.name,
                category: contact.category,
                contact_relationship: contact.relationship || contact.contact_relationship,
                organization: contact.organization || null,
                phone: contact.phone || null,
                email: contact.email || null,
                address: contact.address || null,
                photo: contact.photo || null,
                is_emergency: !contact.isEmergency,
                priority_level: contact.priority || contact.priority_level,
                linked_family_member_id: contact.linkedMemberIds?.[0] || contact.linked_family_member_id || null,
                tags: contact.tags || []
            };
            
            const updated = await updateContact(selectedFamily.id, id, contactData);
            setContacts(contacts.map(c => c.id === id ? updated : c));
        } catch (error) {
            console.error('Error toggling emergency status:', error);
        }
    };

    const resetContactForm = () => {
        setNewContact({
            name: '',
            category: 'Family',
            relationship: '',
            organization: '',
            email: '',
            phone: '',
            address: '',
            notes: '',
            tags: [],
            photoUrl: null,
            isEmergency: false,
            priority: 'General',
            linkedMemberIds: [],
            visibility: 'Family',
            birthday: '',
            anniversary: '',
            verified: false,
            type: 'Person'
        });
    };

    // Filter contacts
    const filteredContacts = contacts.filter(contact => {
        const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              contact.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              contact.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = filterCategory === 'All' || contact.category === filterCategory;
        const matchesPriority = filterPriority === 'All' || contact.priority === filterPriority;
        const matchesEmergency = !showEmergencyOnly || contact.isEmergency;

        return matchesSearch && matchesCategory && matchesPriority && matchesEmergency;
    });

    // Constants
    const categories = [
        'Family', 'Friends', 'Neighbors', 'School', 'College', 'Institutes', 
        'Doctors', 'Hospitals', 'Lawyers', 'Utility Services', 'Hobby/Training', 'Other'
    ];
    
    const priorities = ['General', 'Important', 'Emergency'];
    const visibilityLevels = ['Private', 'Family', 'Public'];

    const getCategoryIcon = (category) => {
        const icons = {
            Family: Users,
            Friends: Heart,
            Neighbors: Home,
            School: GraduationCap,
            College: GraduationCap,
            Institutes: Building,
            Doctors: Stethoscope,
            Hospitals: Building,
            Lawyers: Briefcase,
            'Utility Services': Wrench,
            'Hobby/Training': Star,
            Other: Contact
        };
        return icons[category] || Contact;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            Emergency: 'text-red-600 bg-red-100',
            Important: 'text-orange-600 bg-orange-100',
            General: 'text-gray-600 bg-gray-100'
        };
        return colors[priority] || colors.General;
    };

    const emergencyContacts = contacts.filter(c => c.isEmergency);
    const familyMembers = selectedFamily?.members?.filter(m => m && typeof m === 'object' && m.name) || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            {/* Navigation Bar */}
            <NavBar
                families={families}
                selectedFamily={selectedFamily}
                setSelectedFamily={setSelectedFamily}
                setShowPremiumModal={setShowPremiumModal}
                handleLogout={handleLogout}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Contact Book</span>
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Contact className="w-10 h-10 text-blue-600" />
                            Contact Book
                        </h1>
                        <p className="text-gray-600 mt-1">Family contacts, institutions, and services</p>
                    </div>
                    <button
                        onClick={() => setShowAddContactModal(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md hover:shadow-lg transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        Add Contact
                    </button>
                </div>

                {/* Emergency Contacts Banner */}
                {emergencyContacts.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-red-900 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                Emergency Contacts ({emergencyContacts.length})
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {emergencyContacts.slice(0, 6).map(contact => (
                                <div key={contact.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-200">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">
                                        {contact.name.charAt(0)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm truncate">{contact.name}</p>
                                        <p className="text-xs text-gray-600 truncate">{contact.phone}</p>
                                    </div>
                                    <a
                                        href={`tel:${contact.phone}`}
                                        className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                                    >
                                        <Phone className="w-4 h-4" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6 space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search contacts..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                            >
                                <Users className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                            >
                                <FileText className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4">
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="All">All Categories</option>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>

                        <select
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="All">All Priorities</option>
                            {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>

                        <button
                            onClick={() => setShowEmergencyOnly(!showEmergencyOnly)}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                showEmergencyOnly 
                                    ? 'bg-red-100 text-red-600 border-2 border-red-300' 
                                    : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                            }`}
                        >
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            Emergency Only
                        </button>
                    </div>
                </div>

                {/* Contact Count */}
                <div className="mb-4 text-gray-600">
                    {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''} found
                </div>

                {/* Contacts Display */}
                {filteredContacts.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-12 text-center">
                        <Contact className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No contacts found</p>
                        <button
                            onClick={() => setShowAddContactModal(true)}
                            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Add Your First Contact
                        </button>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredContacts.map(contact => {
                            const Icon = getCategoryIcon(contact.category);
                            return (
                                <div key={contact.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            {contact.photoUrl ? (
                                                <img src={contact.photoUrl} alt={contact.name} className="w-16 h-16 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
                                                    {contact.name.charAt(0)}
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="font-bold text-lg">{contact.name}</h3>
                                                <p className="text-sm text-gray-600">{contact.relationship || contact.category}</p>
                                            </div>
                                        </div>
                                        {contact.isEmergency && (
                                            <AlertCircle className="w-5 h-5 text-red-600" />
                                        )}
                                    </div>

                                    <div className="space-y-2 mb-4">
                                        {contact.phone && (
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Phone className="w-4 h-4 text-gray-400" />
                                                <a href={`tel:${contact.phone}`} className="hover:text-blue-600">{contact.phone}</a>
                                            </div>
                                        )}
                                        {contact.email && (
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Mail className="w-4 h-4 text-gray-400" />
                                                <a href={`mailto:${contact.email}`} className="hover:text-blue-600 truncate">{contact.email}</a>
                                            </div>
                                        )}
                                        {contact.organization && (
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Building className="w-4 h-4 text-gray-400" />
                                                <span className="truncate">{contact.organization}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t">
                                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(contact.priority)}`}>
                                            {contact.priority}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingContact(contact);
                                                    setNewContact(contact);
                                                    setShowAddContactModal(true);
                                                }}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteContact(contact.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredContacts.map(contact => {
                            const Icon = getCategoryIcon(contact.category);
                            return (
                                <div key={contact.id} className="bg-white rounded-lg shadow-md p-4 flex items-center gap-4 hover:shadow-lg transition-shadow">
                                    {contact.photoUrl ? (
                                        <img src={contact.photoUrl} alt={contact.name} className="w-12 h-12 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                                            {contact.name.charAt(0)}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-lg">{contact.name}</h3>
                                            {contact.isEmergency && (
                                                <AlertCircle className="w-4 h-4 text-red-600" />
                                            )}
                                            <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(contact.priority)}`}>
                                                {contact.priority}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                            <span>{contact.category}</span>
                                            {contact.phone && (
                                                <>
                                                    <span>•</span>
                                                    <span>{contact.phone}</span>
                                                </>
                                            )}
                                            {contact.email && (
                                                <>
                                                    <span>•</span>
                                                    <span className="truncate">{contact.email}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {contact.phone && (
                                            <a
                                                href={`tel:${contact.phone}`}
                                                className="p-2 bg-green-100 text-green-600 rounded hover:bg-green-200"
                                            >
                                                <Phone className="w-5 h-5" />
                                            </a>
                                        )}
                                        <button
                                            onClick={() => {
                                                setEditingContact(contact);
                                                setNewContact(contact);
                                                setShowAddContactModal(true);
                                            }}
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteContact(contact.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add/Edit Contact Modal */}
            {showAddContactModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingContact ? 'Edit Contact' : 'Add Contact'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddContactModal(false);
                                    setEditingContact(null);
                                    resetContactForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Photo Upload */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Contact Photo</label>
                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoSelect}
                                    className="hidden"
                                />
                                <div className="flex items-center gap-4">
                                    {newContact.photoUrl ? (
                                        <img src={newContact.photoUrl} alt="Contact" className="w-20 h-20 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
                                            {newContact.name ? newContact.name.charAt(0) : '?'}
                                        </div>
                                    )}
                                    <button
                                        onClick={() => photoInputRef.current?.click()}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300"
                                    >
                                        Choose Photo
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={newContact.name}
                                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Contact name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Type</label>
                                    <select
                                        value={newContact.type}
                                        onChange={(e) => setNewContact({ ...newContact, type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="Person">Person</option>
                                        <option value="Institution">Institution</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Category</label>
                                    <select
                                        value={newContact.category}
                                        onChange={(e) => setNewContact({ ...newContact, category: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Relationship</label>
                                    <input
                                        type="text"
                                        value={newContact.relationship}
                                        onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="e.g., Uncle, Teacher"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Organization</label>
                                    <input
                                        type="text"
                                        value={newContact.organization}
                                        onChange={(e) => setNewContact({ ...newContact, organization: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Company/School/Hospital"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Phone</label>
                                    <input
                                        type="tel"
                                        value={newContact.phone}
                                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="+91 98765 43210"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={newContact.email}
                                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="email@example.com"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Address</label>
                                    <input
                                        type="text"
                                        value={newContact.address}
                                        onChange={(e) => setNewContact({ ...newContact, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Full address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Priority</label>
                                    <select
                                        value={newContact.priority}
                                        onChange={(e) => setNewContact({ ...newContact, priority: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Visibility</label>
                                    <select
                                        value={newContact.visibility}
                                        onChange={(e) => setNewContact({ ...newContact, visibility: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {visibilityLevels.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newContact.isEmergency}
                                            onChange={(e) => setNewContact({ ...newContact, isEmergency: e.target.checked })}
                                            className="w-4 h-4 text-red-600"
                                        />
                                        <span className="text-sm font-medium">Mark as Emergency Contact</span>
                                    </label>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Notes</label>
                                    <textarea
                                        value={newContact.notes}
                                        onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Additional notes..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                            <button
                                onClick={() => {
                                    setShowAddContactModal(false);
                                    setEditingContact(null);
                                    resetContactForm();
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingContact ? handleUpdateContact : handleAddContact}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                {editingContact ? 'Update' : 'Add'} Contact
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactBook;

